/**
 * The Fitness Village mobile menu. Canvas: a 300px panel from the right on a 45% scrim,
 * 16/18px padding, 16px nav rows, 48px auth buttons (radius 12), the attribution at the foot.
 */
import styled from '@emotion/styled';
import { Box, ButtonBase, Drawer } from '@mui/material';
import { withAlpha } from '../../../../Utils/dfcBrand';
import { SurfaceProp, focusRing, omit } from '../style';

type Variant = { variant: 'solid' | 'outline' };

export const MenuPanel = styled(Drawer, omit('surface'))<SurfaceProp>`
  & .MuiDrawer-paper {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 300px;
    max-width: 86vw;
    padding: 16px 18px 22px;
    background: ${({ surface }) => surface.panel};
    background-image: none;
    color: ${({ surface }) => surface.ink};
    border-left: 1px solid ${({ surface }) => surface.border};
  }

  & .MuiBackdrop-root {
    background: ${({ surface }) => withAlpha(surface.scrim, surface.dark ? 0.6 : 0.45)};
  }
`;

export const MenuTop = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const CloseButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    width: 44px;
    height: 44px;
    margin: -6px -6px -6px 0;
    border-radius: 999px;
    color: ${({ surface }) => surface.ink};
    ${({ surface }) => focusRing(surface)}

    &::before {
      content: '';
      position: absolute;
      inset: 6px;
      border-radius: 999px;
      background: ${({ surface }) => surface.track};
    }

    svg {
      position: relative;
      width: 16px;
      height: 16px;
    }
  }
`;

export const NavList = styled('nav')`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const NavRow = styled(ButtonBase, omit('surface', 'active'))<
  SurfaceProp & { active: boolean }
>`
  && {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 48px;
    padding: 0 2px;
    border-bottom: 1px solid ${({ surface }) => surface.headerBorder};
    font-family: inherit;
    font-size: 16px;
    font-weight: 700;
    text-align: left;
    color: ${({ surface }) => surface.ink};
    ${({ surface }) => focusRing(surface)}

    svg {
      width: 16px;
      height: 16px;
      color: ${({ surface, active }) => (active ? surface.accentMark : surface.ink3)};

      /* The forward arrow points the way the page reads. */
      [dir='rtl'] & {
        transform: scaleX(-1);
      }
    }
  }
`;

export const AuthActions = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MenuButton = styled(ButtonBase, omit('surface', 'variant'))<SurfaceProp & Variant>`
  && {
    height: 48px;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    background: ${({ surface, variant }) => (variant === 'solid' ? surface.ctaBg : 'transparent')};
    color: ${({ surface, variant }) => (variant === 'solid' ? surface.ctaFg : surface.ink)};
    border: ${({ surface, variant }) =>
      variant === 'solid' ? 'none' : `1px solid ${surface.border}`};
    ${({ surface }) => focusRing(surface)}
  }
`;

/** Muted in the canvas (2.3:1); secondary ink keeps the small print legible. */
export const Footnote = styled('span', omit('surface'))<SurfaceProp>`
  margin-top: auto;
  font-size: 10px;
  line-height: 1.5;
  color: ${({ surface }) => surface.ink2};
`;
